import { ARMOUR_SLOTS, findItem, type GearSlot, getItem, type Quality, type WeaponSlot, WEAPON_SLOTS } from '@wyn/content';
import { spendBar } from './bars.ts';
import { balance, HOUR } from './config/balance.ts';
import { assertGame } from './errors.ts';
import { assertCan } from './guards.ts';
import { attackCostCut, fightStatMult } from './mastery.ts';
import { fightRenown } from './rewards.ts';
import { activeMinigame } from './minigames/session.ts';
import { type ActionResult, type CombatStats, type GameEvent, type LedgerDelta, maxLife, type PlayerState, playerLevel, townName } from './player.ts';
import { skillProgress } from './progression.ts';
import { createRng, type Rng, type RngState } from './rng.ts';
import { assertReachable } from './sanction.ts';
import { infirmaryDuration } from './states.ts';

/** What a player has worn and wielded, slot by slot. A defender fights with it as saved. */
export type Loadout = Partial<Record<GearSlot, { itemKey: string; quality: Quality }>>;

/** The attacker picks one of these each turn. Fists are always to hand. */
export type AttackSlot = WeaponSlot | 'fists';
export const ATTACK_SLOTS: readonly AttackSlot[] = [...WEAPON_SLOTS, 'fists'];

export type FightChoice = 'spare' | 'rob' | 'maim';
export const FIGHT_CHOICES: readonly FightChoice[] = ['spare', 'rob', 'maim'];

/** The pointer on the attacker to the fight they are in. */
export interface ActiveFight {
  fightId: string;
  defenderId: string;
  defenderName: string;
  startedAt: number;
  expiresAt: number;
}

/** One pack line, as the API reads it. */
export interface PackLine {
  itemKey: string;
  quality: Quality;
  quantity: number;
}

export interface Weapon {
  slot: AttackSlot;
  /** Null for fists. */
  itemKey: string | null;
  quality: Quality | null;
  name: string;
  damage: number;
  accuracy: number;
  /** Ammo it shoots, from the pack (ranged). */
  ammo: string | null;
}

/** A side of a fight, fixed when it starts: stats after perks, gear, and Life as it stood. */
export interface Combatant {
  id: string;
  name: string;
  calling: PlayerState['calling'];
  level: number;
  stats: CombatStats;
  life: number;
  maxLife: number;
  weapons: Partial<Record<AttackSlot, Weapon>>;
  /** Fraction of each hit turned aside by the armour worn (all five pieces and a shield), to the cap. */
  armour: number;
  archery: number;
  /** Shots left for the ranged weapon, of the quality held most of. */
  ammo: { itemKey: string; quality: Quality; count: number } | null;
}

export interface Strike {
  by: 'attacker' | 'defender';
  slot: AttackSlot;
  weapon: string;
  hit: boolean;
  damage: number;
  /** The target's Life after the strike. */
  lifeAfter: number;
}

export interface FightTurn {
  turn: number;
  strikes: Strike[];
}

/** `won` waits for the attacker to choose Spare, Rob or Maim; the rest settle at once. */
export type FightStatus = 'fighting' | 'won' | 'lost' | 'stalemate';

/** A fight in progress, as the server keeps it (`FIGHT#<id>`). Only the attacker drives it. */
export interface FightSession {
  id: string;
  attackerId: string;
  defenderId: string;
  town: PlayerState['town'];
  rng: RngState;
  attacker: Combatant;
  defender: Combatant;
  /** Turns fought so far; also the write guard, as `step` is for minigames. */
  turn: number;
  status: FightStatus;
  log: FightTurn[];
  /** Shots each side has loosed, spent from the pack when the fight settles. */
  shots: { attacker: number; defender: number };
  startedAt: number;
  expiresAt: number;
}

const SELF = 'SELF';

export const activeFight = (p: PlayerState, now: number): ActiveFight | null => (p.fight && p.fight.expiresAt > now ? p.fight : null);

/** Under level 5, or in the first 72 hours: nobody may attack you. You may still attack others. */
export function protectedUntil(p: PlayerState): { level: boolean; until: number } {
  return { level: playerLevel(p) < balance.protection.minLevel, until: p.createdAt + balance.protection.newbieHours * HOUR };
}

export function isProtected(p: PlayerState, now: number): boolean {
  const pr = protectedUntil(p);
  return pr.level || pr.until > now;
}

// ---- Loadout ----

export interface EquipOutcome {
  slot: GearSlot;
  item: { itemKey: string; quality: Quality } | null;
  loadout: Loadout;
}

/** Put something in a slot (or empty it). It must be in the pack and made for that slot. */
export function equip(player: PlayerState, pack: readonly PackLine[], slot: GearSlot, item: { itemKey: string; quality: Quality } | null, now: number): ActionResult<EquipOutcome> {
  assertGame(!activeFight(player, now), 'BLOCKED_BY_STATE', 'Not mid-fight.');
  const loadout: Loadout = { ...player.loadout };
  if (item) {
    const def = findItem(item.itemKey);
    assertGame(def, 'NOT_FOUND', 'No such item.');
    assertGame(def.slot === slot, 'INVALID', `${def.name} does not go there.`);
    assertGame(held(pack, item.itemKey, item.quality) > 0, 'NOT_ENOUGH', `You have no ${def.name.toLowerCase()} of that quality.`);
    loadout[slot] = { itemKey: item.itemKey, quality: item.quality };
  } else {
    delete loadout[slot];
  }
  return { player: { ...player, loadout }, ledger: [], events: [], outcome: { slot, item, loadout } };
}

const held = (pack: readonly PackLine[], itemKey: string, quality: Quality) =>
  pack.find((l) => l.itemKey === itemKey && l.quality === quality)?.quantity ?? 0;

/** The saved loadout, less anything no longer in the pack. */
export function wornLoadout(loadout: Loadout | undefined, pack: readonly PackLine[]): Loadout {
  const out: Loadout = {};
  for (const [slot, g] of Object.entries(loadout ?? {}) as [GearSlot, Loadout[GearSlot]][]) if (g && held(pack, g.itemKey, g.quality) > 0) out[slot] = g;
  return out;
}

// ---- Combatants ----

function weaponFor(slot: AttackSlot, gear: Loadout[GearSlot]): Weapon | null {
  if (slot === 'fists') return { slot, itemKey: null, quality: null, name: 'fists', ...balance.fight.fists, ammo: null };
  if (!gear) return null;
  const item = getItem(gear.itemKey);
  const c = item.combat ?? {};
  // A shield in the off hand is worn, not swung.
  if (!c.damage) return null;
  const q = balance.fight.qualityMult[gear.quality];
  return { slot, itemKey: item.key, quality: gear.quality, name: item.name.toLowerCase(), damage: (c.damage ?? 0) * q, accuracy: c.accuracy ?? 0, ammo: c.ammo ?? null };
}

/** Fix a (refreshed) player's side of a fight: perks, mastery and mount on stats, gear they still hold, ammo, Life now. */
export function combatant(p: PlayerState, pack: readonly PackLine[], archeryXp: number): Combatant {
  const worn = wornLoadout(p.loadout, pack);
  const perks = balance.perks;
  // A mount carries its rider in quicker: its speed, scaled by quality, is added to Speed.
  const mount = worn.mount;
  const mountSpeed = mount ? (getItem(mount.itemKey).combat?.speed ?? 0) * balance.fight.qualityMult[mount.quality] : 0;
  const stats: CombatStats = {
    strength: p.stats.strength * fightStatMult(p, 'strength'),
    defence: p.stats.defence * (p.calling === 'knight' ? perks.knightDefence : 1) * fightStatMult(p, 'defence'),
    speed: p.stats.speed * (1 + mountSpeed) * fightStatMult(p, 'speed'),
    dexterity: p.stats.dexterity * (p.calling === 'ranger' ? perks.rangerDexterity : 1) * fightStatMult(p, 'dexterity'),
  };
  const weapons: Combatant['weapons'] = {};
  for (const slot of ATTACK_SLOTS) {
    const w = weaponFor(slot, worn[slot as GearSlot]);
    if (w) weapons[slot] = w;
  }
  // A bow looses the best arrows held (their head adds to each hit, scaled by quality), then the most held.
  let ammo: Combatant['ammo'] = null;
  const shoots = weapons.ranged?.ammo;
  if (shoots) {
    const bonus = (l: PackLine) => (getItem(l.itemKey).combat?.damage ?? 0) * balance.fight.qualityMult[l.quality];
    const best = pack
      .filter((l) => l.quantity > 0 && findItem(l.itemKey)?.ammo === shoots)
      .sort((a, b) => bonus(b) - bonus(a) || b.quantity - a.quantity)[0];
    if (best) {
      ammo = { itemKey: best.itemKey, quality: best.quality, count: best.quantity };
      weapons.ranged = { ...weapons.ranged!, damage: weapons.ranged!.damage + bonus(best) };
    }
  }
  const armour = Math.min(
    balance.fight.damage.maxArmour,
    [...ARMOUR_SLOTS, 'offHand' as const].reduce((sum, s) => {
      const g = worn[s];
      return g ? sum + (getItem(g.itemKey).combat?.armour ?? 0) * balance.fight.qualityMult[g.quality] : sum;
    }, 0),
  );
  return {
    id: p.id,
    name: p.name,
    calling: p.calling,
    level: playerLevel(p),
    stats,
    life: p.bars.life.value,
    maxLife: maxLife(p),
    weapons,
    armour,
    archery: skillProgress(archeryXp).level,
    ammo,
  };
}

// ---- The maths ----

const clamp = (lo: number, hi: number, x: number) => Math.min(hi, Math.max(lo, x));

/** Speed against Dexterity, plus the weapon's accuracy, plus Archery for ranged. */
export function hitChance(a: Combatant, d: Combatant, w: Weapon): number {
  const h = balance.fight.hit;
  // Ranged leans on a steady hand as much as a quick one.
  const attack = w.slot === 'ranged' ? (a.stats.speed + a.stats.dexterity) / 2 : a.stats.speed;
  const dodge = d.stats.dexterity;
  const archery = w.slot === 'ranged' ? h.archeryPerLevel * (a.archery - 1) : 0;
  return clamp(h.min, h.max, h.base + w.accuracy + archery + (h.spread * (attack - dodge)) / Math.max(1, attack + dodge));
}

/** Damage before jitter: weapon, Strength against Defence, then armour. */
export function baseDamage(a: Combatant, d: Combatant, w: Weapon): number {
  const k = balance.fight.damage;
  const str = Math.max(1, a.stats.strength);
  const ratio = clamp(k.ratioMin, k.ratioMax, Math.pow(str / Math.max(1, d.stats.defence), k.ratioExponent));
  return w.damage * k.scale * Math.sqrt(str / balance.start.stat) * ratio * (1 - d.armour);
}

const canUse = (c: Combatant, w: Weapon | undefined, shotsLoosed: number): w is Weapon =>
  !!w && (!w.ammo || (c.ammo !== null && c.ammo.count > shotsLoosed));

/** The defender has no say: each turn they swing whatever does the most harm on average. */
export function defenderWeapon(s: FightSession): Weapon {
  const { defender: d, attacker: a } = s;
  let best = d.weapons.fists!;
  for (const slot of ATTACK_SLOTS) {
    const w = d.weapons[slot];
    if (canUse(d, w, s.shots.defender) && hitChance(d, a, w) * baseDamage(d, a, w) > hitChance(d, a, best) * baseDamage(d, a, best)) best = w;
  }
  return best;
}

function strike(rng: Rng, by: Strike['by'], a: Combatant, d: Combatant, w: Weapon, targetLife: number): Strike {
  const hit = rng.chance(hitChance(a, d, w));
  const j = balance.fight.damage.jitter;
  const damage = hit ? Math.max(1, Math.round(baseDamage(a, d, w) * (1 - j + 2 * j * rng.next()))) : 0;
  return { by, slot: w.slot, weapon: w.name, hit, damage, lifeAfter: Math.max(0, targetLife - damage) };
}

/** Life each side has left in the fight, from the log. */
export function lifeLeft(s: FightSession): { attacker: number; defender: number } {
  let attacker = s.attacker.life;
  let defender = s.defender.life;
  for (const t of s.log) for (const x of t.strikes) x.by === 'attacker' ? (defender = x.lifeAfter) : (attacker = x.lifeAfter);
  return { attacker, defender };
}

// ---- Starting, turns, settling ----

/** Vigour an attack takes, after the Martial mastery. */
export const attackCost = (p: Pick<PlayerState, 'mastery'>) => balance.fight.vigour - attackCostCut(p);

export interface AttackOutcome {
  fightId: string;
  defender: string;
  vigourSpent: number;
}

/** The UTC day an attack counts towards, for the per-victim cap (the same day as the rollover's). */
export const attackDay = (now: number) => new Date(now).toISOString().slice(0, 10);

/**
 * Pick a fight. Both players must be refreshed to `now`. The attacker pays 25 Vigour up front;
 * the defender is only read: nothing is written to them until the fight settles.
 * `attacksToday` is how many fights the attacker has already started against this defender on
 * `attackDay(now)`; the API counts them in the same transaction.
 */
export function startFight(
  attacker: PlayerState,
  defender: PlayerState,
  input: {
    fightId: string;
    seed: string;
    now: number;
    attackerPack: readonly PackLine[];
    defenderPack: readonly PackLine[];
    attackerArcheryXp: number;
    defenderArcheryXp: number;
    attacksToday: number;
  },
): ActionResult<AttackOutcome> & { session: FightSession } {
  const { now } = input;
  const cost = attackCost(attacker);
  assertCan(attacker, 'fight');
  assertGame(attacker.id !== defender.id, 'INVALID', 'You cannot fight yourself.');
  assertReachable(defender, now);
  assertGame(!activeFight(attacker, now), 'CONFLICT', 'Finish the fight you are in first.');
  assertGame(!activeMinigame(attacker, now), 'BLOCKED_BY_STATE', 'Finish or abandon your game first.');
  assertGame(attacker.bars.vigour.value >= cost, 'NOT_ENOUGH', `An attack takes ${cost} Vigour.`);
  assertGame(defender.status.kind !== 'travelling', 'WRONG_PLACE', `${defender.name} is on the road.`);
  assertGame(defender.town === attacker.town, 'WRONG_PLACE', `${defender.name} is not in ${townName(attacker.town)}.`);
  assertGame(defender.status.kind === 'idle', 'BLOCKED_BY_STATE', defender.status.kind === 'gaol' ? `${defender.name} is in the gaol.` : `${defender.name} is laid up in the Infirmary.`);
  const pr = protectedUntil(defender);
  assertGame(
    !pr.level && pr.until <= now,
    'FORBIDDEN',
    pr.level ? `${defender.name} is under the Crown’s protection until level ${balance.protection.minLevel}.` : `${defender.name} is new to the realm and under the Crown’s protection.`,
  );
  assertGame(
    input.attacksToday < balance.fight.perVictimPerDay,
    'FORBIDDEN',
    `You have picked ${balance.fight.perVictimPerDay} fights with ${defender.name} today. Leave them be until tomorrow.`,
  );

  const expiresAt = now + balance.fight.sessionMs;
  const session: FightSession = {
    id: input.fightId,
    attackerId: attacker.id,
    defenderId: defender.id,
    town: attacker.town,
    rng: createRng(input.seed).state(),
    attacker: combatant(attacker, input.attackerPack, input.attackerArcheryXp),
    defender: combatant(defender, input.defenderPack, input.defenderArcheryXp),
    turn: 0,
    status: 'fighting',
    log: [],
    shots: { attacker: 0, defender: 0 },
    startedAt: now,
    expiresAt,
  };
  return {
    player: {
      ...attacker,
      bars: { ...attacker.bars, vigour: spendBar(attacker.bars.vigour, cost) },
      fight: { fightId: session.id, defenderId: defender.id, defenderName: defender.name, startedAt: now, expiresAt },
    },
    ledger: [],
    events: [],
    outcome: { fightId: session.id, defender: defender.name, vigourSpent: cost },
    session,
  };
}

export function assertFightLive(s: FightSession | null, attackerId: string, now: number): asserts s is FightSession {
  assertGame(s && s.attackerId === attackerId, 'NOT_FOUND', 'No such fight.');
  assertGame(s.expiresAt > now, 'NOT_FOUND', 'That fight has gone cold.');
}

/**
 * One turn: the attacker swings the slot they chose, the defender their best, the quicker first.
 * Pure. Ends the fight when someone drops or the turns run out.
 */
export function fightTurn(s: FightSession, slot: AttackSlot): FightSession {
  assertGame(s.status === 'fighting', 'INVALID', 'The fight is over.');
  const mine = s.attacker.weapons[slot];
  assertGame(mine, 'INVALID', slot === 'fists' ? 'No fists?' : 'You have nothing in that hand.');
  assertGame(canUse(s.attacker, mine, s.shots.attacker), 'NOT_ENOUGH', 'You are out of arrows.');
  const theirs = defenderWeapon(s);
  const rng = createRng(s.rng);
  const life = lifeLeft(s);
  const strikes: Strike[] = [];
  const shots = { ...s.shots };
  const order: Strike['by'][] = s.defender.stats.speed > s.attacker.stats.speed ? ['defender', 'attacker'] : ['attacker', 'defender'];
  for (const by of order) {
    if (life.attacker <= 0 || life.defender <= 0) break;
    if (by === 'attacker') {
      const x = strike(rng, by, s.attacker, s.defender, mine, life.defender);
      if (mine.ammo) shots.attacker++;
      life.defender = x.lifeAfter;
      strikes.push(x);
    } else {
      const x = strike(rng, by, s.defender, s.attacker, theirs, life.attacker);
      if (theirs.ammo) shots.defender++;
      life.attacker = x.lifeAfter;
      strikes.push(x);
    }
  }
  const turn = s.turn + 1;
  const status: FightStatus = life.defender <= 0 ? 'won' : life.attacker <= 0 ? 'lost' : turn >= balance.fight.maxTurns ? 'stalemate' : 'fighting';
  return { ...s, turn, status, shots, rng: rng.state(), log: [...s.log, { turn, strikes }] };
}

export type FightResult = 'won' | 'lost' | 'stalemate' | 'withdrew';

export interface FightSettlement {
  fightId: string;
  result: FightResult;
  choice: FightChoice | null;
  attacker: string;
  defender: string;
  turns: number;
  /** Life each side lost. */
  damageDealt: number;
  damageTaken: number;
  renown: number;
  coin: number;
  prestige: number;
  /** When the loser's Infirmary stay ends; null when nobody went. */
  infirmaryUntil: number | null;
  /** Set when the loser had left (or landed somewhere) before the winner chose: no stay, no robbery. */
  escaped: boolean;
  text: string;
}

export interface FightReport {
  fightId: string;
  attacker: string;
  defender: string;
  result: FightResult;
  choice: FightChoice | null;
  turns: FightTurn[];
}

/** Both sides' writes when a fight ends: two players, two ledgers, an event in each log. */
export interface FightSettled {
  attacker: ActionResult<FightSettlement>;
  defender: { player: PlayerState; ledger: LedgerDelta[]; events: GameEvent[] };
}

const lifeAfter = (p: PlayerState, lost: number) => ({ value: Math.max(1, p.bars.life.value - lost), at: p.bars.life.at });

/** Ammo loosed in the fight, capped at what is still in the pack (it may have been sold meanwhile). */
function ammoSpent(c: Combatant, shots: number, pack: readonly PackLine[], reason: string, counterparty: string): LedgerDelta[] {
  if (!c.ammo || shots === 0) return [];
  const n = Math.min(shots, held(pack, c.ammo.itemKey, c.ammo.quality));
  return n > 0 ? [{ kind: 'item', itemKey: c.ammo.itemKey, quality: c.ammo.quality, delta: -n, reason, counterparty }] : [];
}

/**
 * Settle a fight. Both players are refreshed to `now` and loaded fresh, so Life lost comes off what
 * each has now (never below 1). `choice` is required for a win and ignored otherwise; `withdraw`
 * ends a fight that is still going. `rng` rolls the robbery.
 */
export function settleFight(
  attacker: PlayerState,
  defender: PlayerState,
  s: FightSession,
  input: { choice?: FightChoice; withdraw?: boolean; rng: Rng; now: number; attackerPack: readonly PackLine[]; defenderPack: readonly PackLine[] },
): FightSettled {
  const { now } = input;
  assertGame(s.status !== 'fighting' || input.withdraw, 'INVALID', 'The fight is not over.');
  const result: FightResult = s.status === 'fighting' ? 'withdrew' : s.status;
  const choice = result === 'won' ? (input.choice ?? null) : null;
  assertGame(result !== 'won' || choice, 'INVALID', 'Choose: spare, rob or maim.');

  const left = lifeLeft(s);
  const damageDealt = s.defender.life - left.defender;
  const damageTaken = s.attacker.life - left.attacker;
  const defenderRef = `PLAYER:${defender.id}`;
  const attackerRef = `PLAYER:${attacker.id}`;

  let a: PlayerState = { ...attacker, fight: null, bars: { ...attacker.bars, life: lifeAfter(attacker, damageTaken) } };
  let d: PlayerState = { ...defender, bars: { ...defender.bars, life: lifeAfter(defender, damageDealt) } };
  const aLedger = ammoSpent(s.attacker, s.shots.attacker, input.attackerPack, 'fight:ammo', SELF);
  const dLedger = ammoSpent(s.defender, s.shots.defender, input.defenderPack, 'fight:ammo', SELF);

  let renown = 0;
  let coin = 0;
  let prestige = 0;
  let infirmaryUntil: number | null = null;
  // The loser walked off (or was taken elsewhere) before the winner chose: they keep their purse and their bed.
  const escaped = result === 'won' && (d.status.kind !== 'idle' || d.town !== s.town);
  const f = balance.fight;

  if (result === 'won' && choice) {
    const base = fightRenown(s.defender.level);
    renown = Math.round(choice === 'spare' ? base * f.renown.spareMultiplier * (attacker.calling === 'knight' ? balance.perks.knightSpareRenown : 1) : base);
    prestige = f.prestige[choice];
    if (!escaped) {
      infirmaryUntil = now + infirmaryDuration(d, f.infirmaryMinutes[choice]);
      d = { ...d, status: { kind: 'infirmary', endsAt: infirmaryUntil, reason: choice === 'maim' ? `Maimed by ${attacker.name}` : `Beaten by ${attacker.name}` } };
      if (choice === 'rob') {
        const share = input.rng.next() * (f.rob.max - f.rob.min) + f.rob.min + (attacker.calling === 'rogue' ? balance.perks.rogueRob : 0);
        coin = Math.floor(d.purse * share);
        if (coin > 0) {
          d = { ...d, purse: d.purse - coin };
          a = { ...a, purse: a.purse + coin };
          dLedger.push({ kind: 'coin', account: 'purse', delta: -coin, reason: 'robbed', counterparty: attackerRef });
          aLedger.push({ kind: 'coin', account: 'purse', delta: coin, reason: 'robbery', counterparty: defenderRef });
        }
      }
    }
    a = { ...a, renown: a.renown + renown };
  }

  const settlement: FightSettlement = {
    fightId: s.id,
    result,
    choice,
    attacker: attacker.name,
    defender: defender.name,
    turns: s.turn,
    damageDealt,
    damageTaken,
    renown,
    coin,
    prestige,
    infirmaryUntil,
    escaped,
    text: attackerText(defender.name, result, choice, { renown, coin, escaped }),
  };
  const report: FightReport = { fightId: s.id, attacker: attacker.name, defender: defender.name, result, choice, turns: s.log };
  return {
    attacker: { player: a, ledger: aLedger, events: [{ type: 'fight', text: settlement.text, data: { report, role: 'attacker', result, choice }, otherId: defender.id }], outcome: settlement },
    defender: {
      player: d,
      ledger: dLedger,
      events: [{ type: 'fight', text: defenderText(attacker.name, result, choice, { coin, escaped, infirmaryUntil }), data: { report, role: 'defender', result, choice }, otherId: attacker.id }],
    },
  };
}

function attackerText(foe: string, result: FightResult, choice: FightChoice | null, x: { renown: number; coin: number; escaped: boolean }): string {
  if (result === 'lost') return `You attacked ${foe} and were beaten.`;
  if (result === 'stalemate') return `You fought ${foe} to a standstill.`;
  if (result === 'withdrew') return `You broke off your attack on ${foe}.`;
  const tail = x.renown ? ` (+${x.renown} Renown)` : '';
  if (x.escaped) return `You beat ${foe}, but they slipped away before you could ${choice}.${tail}`;
  if (choice === 'spare') return `You beat ${foe} and spared them.${tail}`;
  if (choice === 'rob') return `You beat ${foe} and took ${x.coin} coin from their purse.${tail}`;
  return `You beat ${foe} and maimed them.${tail}`;
}

function defenderText(foe: string, result: FightResult, choice: FightChoice | null, x: { coin: number; escaped: boolean; infirmaryUntil: number | null }): string {
  if (result === 'lost') return `${foe} attacked you and you beat them off.`;
  if (result === 'stalemate') return `${foe} attacked you; neither of you could best the other.`;
  if (result === 'withdrew') return `${foe} attacked you, then thought better of it.`;
  if (x.escaped) return `${foe} beat you in a fight, but you got away.`;
  const bed = x.infirmaryUntil ? ' You are carried to the Infirmary.' : '';
  if (choice === 'spare') return `${foe} beat you in a fight and spared you.${bed}`;
  if (choice === 'rob') return `${foe} beat you in a fight and took ${x.coin} coin from your purse.${bed}`;
  return `${foe} beat you in a fight and maimed you.${bed}`;
}

/** What the attacker sees of a fight: both Life bars, the log, and the weapons they can swing. */
export function fightView(s: FightSession) {
  const life = lifeLeft(s);
  const side = (c: Combatant, l: number) => ({ id: c.id, name: c.name, level: c.level, life: l, maxLife: c.maxLife });
  return {
    id: s.id,
    status: s.status,
    turn: s.turn,
    maxTurns: balance.fight.maxTurns,
    attacker: side(s.attacker, life.attacker),
    defender: side(s.defender, life.defender),
    weapons: ATTACK_SLOTS.flatMap((slot) => {
      const w = s.attacker.weapons[slot];
      return w ? [{ slot, name: w.name, usable: canUse(s.attacker, w, s.shots.attacker), ammo: w.ammo && s.attacker.ammo ? s.attacker.ammo.count - s.shots.attacker : null }] : [];
    }),
    log: s.log,
    expiresAt: s.expiresAt,
  };
}
export type FightView = ReturnType<typeof fightView>;
